
import { motion, useAnimation } from "framer-motion";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent
} from "@/components/ui/dialog";

interface CircleImageProps {
  imageSrc: string;
  className?: string;
  borderColor: string;
  index: number;
  depth: number;
}

export const CircleImage = ({ imageSrc, className, borderColor, index, depth }: CircleImageProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const controls = useAnimation();
  const isEvenPair = Math.floor(index / 2) % 2 === 0;
  const startX = isEvenPair ? -100 : window.innerWidth + 100;
  const endX = isEvenPair ? window.innerWidth + 100 : -100;
  
  // Otimização dos valores baseado na profundidade
  const baseSize = 160;
  const size = baseSize * (1 + (depth * 0.12));
  
  // Otimização do blur e transparência
  const blurAmount = depth * 1.2;
  const opacity = 1 - (depth * 0.1);

  useEffect(() => {
    const animate = async () => {
      await controls.start({
        x: [startX, window.innerWidth / 2, endX],
        borderRadius: ["50%", "25%", "50%"],
        transition: {
          duration: 12 + (index * 1.2) - (depth * 2),
          repeat: Infinity,
          ease: "linear",
          times: [0, 0.5, 1],
        }
      });
    };

    animate();
  }, [controls, startX, endX, index, depth]);

  return (
    <>
      <motion.div
        animate={controls}
        initial={{ x: startX }}
        className={`absolute overflow-hidden cursor-pointer ${className}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderColor,
          borderWidth: "2px",
          background: `rgba(255, 255, 255, ${0.1 * opacity})`,
          backdropFilter: `blur(${5 - blurAmount}px)`,
          boxShadow: `
            0 ${6 + (depth * 3)}px ${16 + (depth * 6)}px -4px rgba(0, 0, 0, ${0.2 + (depth * 0.06)}),
            0 3px 10px -2px ${borderColor}25,
            inset 0 1px 2px rgba(255, 255, 255, ${0.25 * opacity}),
            inset 0 -1px 2px rgba(0, 0, 0, ${0.12 * opacity})
          `,
          y: 80 + (Math.floor(index / 2) * 180) + (depth * 40),
          zIndex: 10 - Math.floor(depth),
          transform: `scale(${1 - (depth * 0.06)})`,
        }}
        whileHover={{ 
          scale: 1.02,
          transition: { duration: 0.15 }
        }}
        onClick={() => setIsOpen(true)}
      >
        <img 
          src={imageSrc} 
          alt="Service illustration"
          className="w-full h-full object-cover contrast-105 brightness-102"
          style={{ filter: `blur(${blurAmount}px)` }}
        />
      </motion.div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-hidden">
          <img 
            src={imageSrc} 
            alt="Service illustration"
            className="w-full h-full object-contain contrast-105 brightness-102"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
